// R1e — A2P 10DLC-compliant Privacy Policy (canonical route /privacy-policy).
// R1-VISUAL Slice 7b — Style-only reskin. All copy (including every Twilio
// canonical phrase) preserved verbatim. Content mandated by Twilio Trust
// Hub review; do not soften SMS clauses.
import React from "react";
import { Link } from "react-router-dom";
import PublicShell, { usePublicContent } from "../../components/public/PublicShell";

const POLICY_VERSION = "v1.0";
const EFFECTIVE = "September 23, 2026";

const LOGO = "/brand/erf_logo_horizontal.jpg";

// Brand tokens (shared with landing / About / login / quote).
const T = {
  ivory: "#F5F1E8",
  navy: "#0B2545",
  brass: "#B08D57",
  charcoal: "#1F2937",
  border: "#E5E1DA",
  walnut: "#5B3A1F",
  bgAlt: "#FAF7F2",
};

// Section heading with brass hairline rule + navy serif.
function LegalH2({ id, children, testId }) {
  return (
    <>
      <div
        aria-hidden="true"
        style={{ height: 1, background: T.brass, opacity: 0.55, margin: "48px 0 20px" }}
      />
      <h2
        id={id}
        data-testid={testId}
        style={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: 26,
          fontWeight: 700,
          color: T.navy,
          margin: 0,
          letterSpacing: "-0.005em",
          lineHeight: 1.2,
        }}
      >
        {children}
      </h2>
    </>
  );
}

function LegalH3({ children, marginTop = 32 }) {
  return (
    <>
      <div
        aria-hidden="true"
        style={{ height: 1, background: T.brass, opacity: 0.4, marginTop, marginBottom: 14 }}
      />
      <h3
        style={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: 20,
          fontWeight: 600,
          color: T.navy,
          margin: 0,
          lineHeight: 1.25,
        }}
      >
        {children}
      </h3>
    </>
  );
}

export default function PrivacyPolicy() {
  const c = usePublicContent();
  const supportEmail = c.contact_email || "info@erfenterprisesllc.com";
  const supportOffice = c.contact_phone_office || "(800) 240-6404";
  const supportMobile = c.contact_phone_mobile || "(771) 271-3011";

  return (
    <PublicShell>
      {/* Navy eyebrow band — logo left, page title serif right. Mirrors landing header pattern. */}
      <section
        data-testid="privacy-policy-eyebrow-band"
        style={{ background: T.navy, color: T.ivory, borderBottom: `1px solid ${T.brass}` }}
      >
        <div
          className="mx-auto px-4 md:px-6 lg:px-8"
          style={{
            maxWidth: 1600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 24,
            padding: "22px 24px",
            flexWrap: "wrap",
          }}
        >
          <Link
            to="/"
            aria-label="ERF Enterprises home"
            data-testid="privacy-eyebrow-logo-link"
            style={{ display: "inline-flex", alignItems: "center" }}
          >
            <img
              src={LOGO}
              alt="ERF ENTERPRISES LLC"
              style={{ height: 40, width: "auto", display: "block", background: T.ivory, padding: "4px 10px", borderRadius: 3 }}
            />
          </Link>
          <div
            style={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: 11,
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              color: T.brass,
            }}
          >
            Legal · Privacy Policy
          </div>
        </div>
      </section>

      <section
        data-testid="privacy-policy-body"
        style={{ background: T.ivory, color: T.charcoal }}
      >
        <div
          className="mx-auto px-4 md:px-6 lg:px-8"
          style={{ maxWidth: 1600, padding: "64px 24px 80px" }}
        >
          <div style={{ maxWidth: "72ch", margin: "0 auto" }}>
            <div
              style={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: 11,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                color: T.brass,
              }}
            >
              Legal
            </div>
            <h1
              style={{
                marginTop: 14,
                fontFamily: '"Playfair Display", Georgia, serif',
                fontSize: "clamp(34px, 4.4vw, 46px)",
                fontWeight: 700,
                color: T.navy,
                letterSpacing: "-0.01em",
                lineHeight: 1.12,
              }}
            >
              Privacy Policy
            </h1>
            <p style={{ fontSize: 13, color: T.charcoal, opacity: 0.72, marginTop: 10 }}>
              Version {POLICY_VERSION} · Effective {EFFECTIVE}
            </p>

            <div style={{ fontSize: 16, lineHeight: 1.75, color: T.charcoal, marginTop: 28 }}>
              <p>
                ERF ENTERPRISES LLC (&ldquo;ERF,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;) respects your privacy.
                This Privacy Policy explains what information we collect, how we use it, when we share it, and the
                specific rules that apply to the phone number and SMS opt-in data we handle for our customer
                communications program.
              </p>

              <LegalH3>Information we collect</LegalH3>
              <p>When you contact us, request a quote, or engage our services, we may collect:</p>
              <ul style={{ paddingLeft: 22, marginTop: 8 }}>
                <li style={{ marginBottom: 6 }}><strong>Contact information</strong> — your name, email address, and (if you provide it) your phone or mobile number.</li>
                <li style={{ marginBottom: 6 }}><strong>Project information</strong> — the project location, timeline, description, service interest, and any files or photos you attach.</li>
                <li style={{ marginBottom: 6 }}><strong>Communication content</strong> — the messages, emails, and voicemail transcripts you exchange with us.</li>
                <li><strong>Technical audit data</strong> — timestamps, IP address, and browser user-agent captured when you submit a form, used solely for anti-abuse and compliance recordkeeping.</li>
              </ul>

              <LegalH3>Phone numbers &amp; SMS consent</LegalH3>
              <p>
                Providing a phone number on any ERF form is optional. If you additionally check the box authorizing us to
                text you, you are opting in to receive service-related SMS from ERF ENTERPRISES LLC covering topics such as
                your inquiries, estimates, projects, appointments, service scheduling, account activity, and related
                operational communications.
              </p>
              <p><strong>Message frequency varies.</strong> Message and data rates may apply. You may reply <strong>STOP</strong> at any time to opt out, or <strong>HELP</strong> for support. Reply STOP to opt out. Reply HELP for assistance from ERF ENTERPRISES LLC.</p>
              <blockquote
                data-testid="sms-nonsale-clause"
                style={{
                  borderLeft: `3px solid ${T.brass}`,
                  background: T.bgAlt,
                  padding: "16px 20px",
                  borderRadius: 3,
                  marginTop: 16,
                  marginBottom: 0,
                  color: T.walnut,
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontStyle: "italic",
                  fontSize: 16.5,
                  lineHeight: 1.6,
                }}
              >
                Mobile information and SMS opt-in/consent data will not be sold or shared with third parties or affiliates
                for their marketing or promotional purposes.
              </blockquote>

              <LegalH3>How we use your information</LegalH3>
              <ul style={{ paddingLeft: 22, marginTop: 8 }}>
                <li style={{ marginBottom: 6 }}>To respond to your quote request or inquiry.</li>
                <li style={{ marginBottom: 6 }}>To schedule site visits, deliver estimates, and coordinate active project work.</li>
                <li style={{ marginBottom: 6 }}>To send account, scheduling, and operational messages via the channel you chose (email and, if you opted in, SMS).</li>
                <li>To keep an audit-tracked record of consequential decisions on your project, as required by construction industry recordkeeping standards.</li>
              </ul>

              <LegalH3>Service providers</LegalH3>
              <p>
                We may share your information with the service providers that help us operate our business — for example,
                our email delivery provider, our SMS messaging provider, our hosting provider, and our payment processor —
                only to the extent necessary to provide ERF&rsquo;s services to you and under contractual restrictions that
                prohibit them from using your information for their own marketing.
              </p>

              <LegalH3>What we do not do</LegalH3>
              <ul style={{ paddingLeft: 22, marginTop: 8 }}>
                <li style={{ marginBottom: 6 }}>We do not sell your personal information.</li>
                <li style={{ marginBottom: 6 }}>We do not sell, rent, or share your mobile number or SMS opt-in status with third parties or affiliates for their marketing or promotional purposes.</li>
                <li>We do not use tracking cookies or third-party advertising analytics on this website.</li>
              </ul>

              <LegalH3>Data retention &amp; security</LegalH3>
              <p>
                Customer records are stored in an audit-tracked, tenant-isolated database with encrypted-at-rest credentials
                and immutable audit logging of every consequential change. We retain lead and project data for as long as
                necessary to serve you and to comply with legal record-keeping obligations for a general contractor
                operating in the states we serve.
              </p>

              <LegalH3>Your choices</LegalH3>
              <ul style={{ paddingLeft: 22, marginTop: 8 }}>
                <li style={{ marginBottom: 6 }}>You may decline SMS consent and still submit any of our forms and receive service through email or phone.</li>
                <li style={{ marginBottom: 6 }}>You may opt out of SMS at any time by replying <strong>STOP</strong> to any message we send you.</li>
                <li>You may request access to, correction of, or deletion of your personal information by contacting us using the details below.</li>
              </ul>

              <LegalH3>Responsible business</LegalH3>
              <p>
                ERF ENTERPRISES LLC is the business responsible for this website, its content, and any SMS messaging
                program described here.
              </p>

              <LegalH3>How to contact us</LegalH3>
              <ul style={{ paddingLeft: 22, marginTop: 8 }}>
                <li style={{ marginBottom: 6 }}>Email: <a href={`mailto:${supportEmail}`} style={{ color: T.navy, textDecoration: "underline" }}>{supportEmail}</a></li>
                <li style={{ marginBottom: 6 }}>Office phone: <a href={`tel:+1${supportOffice.replace(/\D/g, "").slice(-10)}`} style={{ color: T.navy, textDecoration: "underline" }}>{supportOffice}</a></li>
                <li style={{ marginBottom: 6 }}>Mobile: <a href={`tel:+1${supportMobile.replace(/\D/g, "").slice(-10)}`} style={{ color: T.navy, textDecoration: "underline" }}>{supportMobile}</a></li>
                <li>See also our <Link to="/terms-and-conditions" style={{ color: T.navy, textDecoration: "underline" }}>Terms &amp; Conditions</Link> for the full SMS/Messaging Terms.</li>
              </ul>

              <p style={{ marginTop: 48, fontSize: 12, color: T.charcoal, fontStyle: "italic", opacity: 0.7 }}>
                We may update this Privacy Policy from time to time. Material changes will be reflected by an updated
                version number and effective date at the top of this page.
              </p>
            </div>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
